import React, { useState } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { ContactHero } from '../components/contact';
import { FloatingPattern } from '../components/common';

const PageContainer = styled.div`
    position: relative;
    padding-top: 70px;
    overflow-x: hidden;
`;

const FormSection = styled.section`
    width: 100%;
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    padding: ${({ theme }) => theme.spacing.xxl} 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: ${({ theme }) => theme.spacing.xl} 0;
    }
`;

const FormContainer = styled.div`
    max-width: 800px;
    margin: 0 auto;
    padding: 0 ${({ theme }) => theme.spacing.xxl};

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0 ${({ theme }) => theme.spacing.md};
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.lg};
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
`;

const Label = styled.label`
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const Input = styled.input`
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    border: 2px solid ${({ theme }) => theme.colors.backgroundDark};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textDark};
    transition: border-color 0.3s ease;

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
    }

    &::placeholder {
        color: ${({ theme }) => theme.colors.textMuted};
    }
`;

const TextArea = styled.textarea`
    padding: ${({ theme }) => theme.spacing.sm};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    border: 2px solid ${({ theme }) => theme.colors.backgroundDark};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textDark};
    min-height: 180px;
    resize: vertical;
    font-family: inherit;
    transition: border-color 0.3s ease;

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primary};
    }

    &::placeholder {
        color: ${({ theme }) => theme.colors.textMuted};
    }
`;

const SubmitButton = styled.button`
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.primary};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    cursor: pointer;
    transition:
        background-color 0.3s ease,
        transform 0.2s ease;
    align-self: center;
    min-width: 200px;

    &:hover {
        background-color: ${({ theme }) => theme.colors.primaryDark};
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        background-color: ${({ theme }) => theme.colors.textMuted};
        cursor: not-allowed;
        transform: none;
    }
`;

const SuccessModal = styled.div<{ $show: boolean }>`
    display: ${({ $show }) => ($show ? 'flex' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    z-index: 1000;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const ModalContent = styled.div`
    background-color: ${({ theme }) => theme.colors.backgroundLighter};
    padding: ${({ theme }) => theme.spacing.xl};
    border-radius: ${({ theme }) => theme.borderRadius.large};
    border: 3px solid ${({ theme }) => theme.colors.text};
    text-align: center;
    max-width: 400px;
    animation: slideUp 0.3s ease;

    @keyframes slideUp {
        from {
            transform: translateY(50px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
`;

const ModalIcon = styled.div`
    font-size: 3rem;
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.primary};
`;

const ModalTitle = styled.h3`
    color: ${({ theme }) => theme.colors.text};
    font-size: 2rem;
    margin: 0 0 ${({ theme }) => theme.spacing.md} 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: 1.5rem;
    }
`;

const ModalMessage = styled.p`
    color: ${({ theme }) => theme.colors.text};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    line-height: 1.5;
    margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
`;

const ModalButton = styled.button`
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.xl};
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    color: ${({ theme }) => theme.colors.text};
    background-color: ${({ theme }) => theme.colors.primary};
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: ${({ theme }) => theme.colors.primaryDark};
    }
`;

const ErrorMessage = styled.div`
    padding: ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius.small};
    background-color: rgba(244, 67, 54, 0.2);
    color: #f44336;
    font-size: ${({ theme }) => theme.fontSizes.paragraph};
    text-align: center;
`;

interface FormData {
    name: string;
    email: string;
    subject: string;
    message: string;
}

export const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            // Send email using EmailJS
            const result = await emailjs.send(
                process.env.REACT_APP_EMAILJS_SERVICE_ID!,
                process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
                {
                    name: formData.name,
                    email: formData.email,
                    subject: formData.subject,
                    message: formData.message,
                },
                process.env.REACT_APP_EMAILJS_PUBLIC_KEY!,
            );

            if (result.status === 200) {
                setShowSuccessModal(true);
                // Reset form
                setFormData({ name: '', email: '', subject: '', message: '' });
            }
        } catch (error) {
            console.error('EmailJS error:', error);
            setErrorMessage(
                'Failed to send message. Please try again or email us directly at butterscotch.boys.music@gmail.com',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = () => {
        setShowSuccessModal(false);
    };

    return (
        <PageContainer>
            <FloatingPattern />
            <ContactHero />

            <FormSection>
                <FormContainer>
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label htmlFor="name">Name *</Label>
                            <Input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="John Doe"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="email">Email *</Label>
                            <Input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="your.email@example.com"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="subject">Subject *</Label>
                            <Input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder="What is your inquiry about?"
                                required
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label htmlFor="message">Message *</Label>
                            <TextArea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Type your message here..."
                                required
                            />
                        </FormGroup>

                        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

                        <SubmitButton type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </SubmitButton>
                    </Form>
                </FormContainer>
            </FormSection>

            <SuccessModal $show={showSuccessModal} onClick={handleCloseModal}>
                <ModalContent onClick={(e) => e.stopPropagation()}>
                    <ModalIcon>✓</ModalIcon>
                    <ModalTitle>Message Sent!</ModalTitle>
                    <ModalMessage>
                        Thank you for reaching out. We've received your message and will get back to
                        you as soon as possible.
                    </ModalMessage>
                    <ModalButton onClick={handleCloseModal}>Close</ModalButton>
                </ModalContent>
            </SuccessModal>
        </PageContainer>
    );
};
